import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { api } from "@/lib/api";
import { pickDefaultSplitAgeGroup } from "@/lib/split-age-group";
import {
  NavBreadcrumbs,
  type BreadcrumbEntry,
} from "@/components/NavBreadcrumbs";
import {
  AgeGroupParticipationChart,
  type AgeGroupParticipationDatum,
} from "@/components/charts/AgeGroupParticipationChart";
import {
  SplitDistributionChart,
  type SplitDistributionRecord,
} from "@/components/charts/SplitDistributionChart";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { PaginationControls } from "@/components/ui/pagination-controls";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import "./events.$eventId.css";

const routeApi = getRouteApi("/events/$eventId");
const EVENT_RESULTS_PER_PAGE = 100;

function isSplitDistributionRecord(
  value: unknown,
): value is SplitDistributionRecord {
  if (!value || typeof value !== "object") return false;
  const candidate = value as {
    distribution?: { buckets?: unknown };
  };
  return Array.isArray(candidate.distribution?.buckets);
}

function shouldHideRank(
  rank: number | null | undefined,
  flags: { dnf?: boolean | null; dns?: boolean | null; dq?: boolean | null },
): boolean {
  if (flags.dnf || flags.dns || flags.dq) return true;
  return rank != null && rank >= 99999;
}

function formatEventDate(eventDate: string | null | undefined): string | null {
  if (!eventDate) return null;
  return new Date(`${eventDate}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatSegmentTypeLabel(segmentType: string): string {
  if (!segmentType) return "Split";
  return segmentType.charAt(0).toUpperCase() + segmentType.slice(1);
}

function getResultStatus(result: {
  dnf?: boolean | null;
  dns?: boolean | null;
  dq?: boolean | null;
  finisher?: boolean | null;
}): string | null {
  if (result.dq) return "DQ";
  if (result.dns) return "DNS";
  if (result.dnf) return "DNF";
  if (result.finisher === false) return "Unfinished";
  return null;
}

type EventSummaryCardProps = {
  label: string;
  value: string;
  detail?: string;
};

function EventSummaryCard({ label, value, detail }: EventSummaryCardProps) {
  return (
    <article className="event-page-summary-card">
      <p className="event-page-summary-label">{label}</p>
      <p className="event-page-summary-value">{value}</p>
      {detail ? <p className="event-page-summary-detail">{detail}</p> : null}
    </article>
  );
}

type EventRecordCardProps = {
  label: string;
  time: string;
  athleteName: string;
  athleteId: string;
  detail?: string;
  tone?: "overall" | "male" | "female" | "split";
};

function EventRecordCard({
  label,
  time,
  athleteName,
  athleteId,
  detail,
  tone = "overall",
}: EventRecordCardProps) {
  return (
    <article className={`event-page-record-card event-page-record-card--${tone}`}>
      <p className="event-page-record-label">{label}</p>
      <p className="event-page-record-value">{time}</p>
      <Link
        to="/athletes/$athleteId"
        params={{ athleteId }}
        className="event-page-record-link"
      >
        {athleteName}
      </Link>
      {detail ? <p className="event-page-record-detail">{detail}</p> : null}
    </article>
  );
}

export default function EventPage() {
  const { eventId } = routeApi.useParams();
  const {
    ageGroup: searchAgeGroup,
    highlight,
    highlightRank,
    page: searchPage,
  } = routeApi.useSearch();
  const navigate = useNavigate({ from: "/events/$eventId" });
  const highlightRef = useRef<HTMLTableRowElement>(null);

  const gender = "";
  const ageGroup = searchAgeGroup ?? "";
  const defaultPage =
    !ageGroup && highlightRank && highlightRank > 0
      ? Math.ceil(highlightRank / EVENT_RESULTS_PER_PAGE)
      : 1;
  const currentPage = Math.max(searchPage ?? defaultPage, 1);
  const offset = (currentPage - 1) * EVENT_RESULTS_PER_PAGE;

  const setAgeGroup = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ageGroup: value || undefined,
        page: undefined,
      }),
      replace: true,
    });
  };

  const setPage = (page: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: page > 1 ? page : undefined,
      }),
      replace: true,
    });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["event", eventId, gender, ageGroup, currentPage],
    queryFn: async () => {
      const { data, error } = await api.GET("/events/{id}", {
        params: {
          path: { id: eventId },
          query: {
            gender: gender || undefined,
            ageGroup: ageGroup || undefined,
            limit: EVENT_RESULTS_PER_PAGE,
            offset,
          },
        },
      });
      if (error) throw error;
      return data;
    },
  });

  const { data: ageGroupsData } = useQuery({
    queryKey: ["event-age-groups", eventId],
    queryFn: async () => {
      const { data, error } = await api.GET("/events/{id}/age-groups", {
        params: { path: { id: eventId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const {
    data: statsData,
    error: statsError,
    isLoading: isStatsLoading,
  } = useQuery({
    queryKey: ["event-stats", eventId],
    queryFn: async () => {
      const { data, error } = await api.GET("/events/{id}/stats", {
        params: { path: { id: eventId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const [splitAgeGroup, setSplitAgeGroup] = useState("");
  const [tableSearch, setTableSearch] = useState("");

  const { data: ageGroupSplitsData, isFetching: ageGroupSplitsFetching } =
    useQuery({
      queryKey: ["event-age-group-splits", eventId, splitAgeGroup],
      queryFn: async () => {
        const { data, error } = await api.GET("/events/{id}/splits", {
          params: {
            path: { id: eventId },
            query: { ageGroup: splitAgeGroup },
          },
        });
        if (error) throw error;
        return data;
      },
      enabled: !!splitAgeGroup,
    });

  const ageGroups = ageGroupsData?.ageGroups ?? [];
  const splitAgeGroupCounts = (statsData?.ageGroupStats ?? []).map((row) => ({
    ageGroup: row.ageGroup,
    count: row.participantCount,
  }));

  useEffect(() => {
    if (highlight && highlightRef.current) {
      highlightRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [highlight, data, tableSearch]);

  useEffect(() => {
    setSplitAgeGroup("");
    setTableSearch("");
  }, [eventId]);

  useEffect(() => {
    if (splitAgeGroup) return;
    if (!statsData) return;
    if (ageGroups.length === 0) return;

    const defaultAgeGroup = pickDefaultSplitAgeGroup(
      ageGroups,
      splitAgeGroupCounts,
    );

    if (defaultAgeGroup) {
      setSplitAgeGroup(defaultAgeGroup);
    }
  }, [splitAgeGroup, statsData, ageGroups, splitAgeGroupCounts]);

  const results = data?.results ?? [];
  const normalizedTableSearch = tableSearch.trim().toLowerCase();
  const filteredResults = useMemo(() => {
    if (!normalizedTableSearch) return results;

    return results.filter((result) => {
      const searchableText = [
        result.athleteName,
        result.ageGroup,
        result.athleteGender,
        result.athleteCity,
        result.athleteState,
        result.athleteCountryIso2,
        result.bib ? String(result.bib) : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedTableSearch);
    });
  }, [results, normalizedTableSearch]);

  if (isLoading) {
    return (
      <div className="event-page">
        <div className="event-page-hero animate-pulse">
          <div className="mb-3 h-8 w-1/3 rounded bg-muted/70"></div>
          <div className="h-4 w-1/4 rounded bg-muted/50"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <p className="text-destructive">Error loading event</p>;
  }

  if (!data) return null;

  const { event } = data;
  const splitDistributionSplits = (
    (ageGroupSplitsData?.splits ?? []) as unknown[]
  ).filter(isSplitDistributionRecord);
  const ageGroupParticipationData: AgeGroupParticipationDatum[] = [
    ...(statsData?.ageGroupStats ?? []),
  ].map((item) => {
    const genderStats = item as typeof item & {
      maleParticipantCount?: number;
      femaleParticipantCount?: number;
    };
    return {
      ageGroup: item.ageGroup,
      maleCount: genderStats.maleParticipantCount ?? 0,
      femaleCount: genderStats.femaleParticipantCount ?? 0,
    };
  });

  const breadcrumbItems: BreadcrumbEntry[] = [
    { label: "Records", to: "/records", search: {} },
  ];
  if (event.eventSeriesId) {
    breadcrumbItems.push({
      label: event.eventSeriesName ?? "Event Series",
      to: "/events/series/$seriesId",
      params: { seriesId: event.eventSeriesId },
    });
  }

  const courseProfile = (event as typeof event & { courseProfile?: { swim?: string | null; bike?: string | null; run?: string | null } | null }).courseProfile ?? null;

  const eventMeta = [
    event.brand,
    event.raceType,
    formatEventDate(event.eventDate),
  ]
    .filter(Boolean)
    .join(" · ");

  const summaryCards: EventSummaryCardProps[] = statsData
    ? [
        {
          label: "Finishers",
          value: statsData.participation.totalFinishers.toLocaleString(),
          detail: "Completed results in this event",
        },
        {
          label: "Field mix",
          value: `${statsData.participation.maleCount.toLocaleString()} / ${statsData.participation.femaleCount.toLocaleString()}`,
          detail: "Men / women finishers",
        },
        {
          label: "Age groups",
          value: ageGroups.length.toLocaleString(),
          detail: "Distinct categories represented",
        },
        {
          label: "Fastest overall",
          value: statsData.fastestOverall?.timeFormatted ?? "Pending",
          detail: statsData.fastestOverall?.athleteName ?? "No finisher record yet",
        },
      ]
    : [
        {
          label: "Race type",
          value: event.raceType ?? "Unknown",
          detail: "Event classification",
        },
        {
          label: "Series",
          value: event.eventSeriesName ?? "Standalone",
          detail: event.eventSeriesId ? "Part of a tracked series" : "Single event",
        },
        {
          label: "Age groups",
          value: ageGroups.length.toLocaleString(),
          detail: "Available result filters",
        },
      ];

  return (
    <div className="event-page">
      <header className="event-page-hero">
        <NavBreadcrumbs items={breadcrumbItems} current={event.name} />
        <div className="event-page-hero-copy">
          <p className="event-page-eyebrow">Event Overview</p>
          <h1 className="event-page-title">{event.name}</h1>
          {eventMeta ? <p className="event-page-meta">{eventMeta}</p> : null}
          {courseProfile ? (
            <div className="event-page-course-profile">
              {courseProfile.swim ? (
                <span className="event-page-course-pill event-page-course-pill--swim">
                  Swim: {courseProfile.swim}
                </span>
              ) : null}
              {courseProfile.bike ? (
                <span className="event-page-course-pill event-page-course-pill--bike">
                  Bike: {courseProfile.bike}
                </span>
              ) : null}
              {courseProfile.run ? (
                <span className="event-page-course-pill event-page-course-pill--run">
                  Run: {courseProfile.run}
                </span>
              ) : null}
            </div>
          ) : null}
          {event.eventSeriesId ? (
            <p className="event-page-submeta">
              Part of{" "}
              <Link
                to="/events/series/$seriesId"
                params={{ seriesId: event.eventSeriesId }}
                className="event-page-inline-link"
              >
                {event.eventSeriesName ?? "event series"}
              </Link>
            </p>
          ) : null}
        </div>
        <div className="event-page-summary-grid">
          {summaryCards.map((card) => (
            <EventSummaryCard key={card.label} {...card} />
          ))}
        </div>
      </header>

      {statsData ? (
        <>
          <section className="event-page-section">
            <div className="event-page-section-header">
              <div>
                <h2 className="event-page-section-title">Event Records</h2>
                <p className="event-page-section-copy">
                  Best benchmark times from this specific event edition.
                </p>
              </div>
            </div>

            <div className="event-page-record-grid">
              {statsData.fastestOverall ? (
                <EventRecordCard
                  label="Fastest Overall"
                  time={statsData.fastestOverall.timeFormatted}
                  athleteName={statsData.fastestOverall.athleteName}
                  athleteId={statsData.fastestOverall.athleteId}
                  tone="overall"
                />
              ) : null}
              {statsData.fastestByGender.male ? (
                <EventRecordCard
                  label="Fastest Male"
                  time={statsData.fastestByGender.male.timeFormatted}
                  athleteName={statsData.fastestByGender.male.athleteName}
                  athleteId={statsData.fastestByGender.male.athleteId}
                  tone="male"
                />
              ) : null}
              {statsData.fastestByGender.female ? (
                <EventRecordCard
                  label="Fastest Female"
                  time={statsData.fastestByGender.female.timeFormatted}
                  athleteName={statsData.fastestByGender.female.athleteName}
                  athleteId={statsData.fastestByGender.female.athleteId}
                  tone="female"
                />
              ) : null}
            </div>

            {statsData.splitRecords.length > 0 ? (
              <div className="event-page-record-grid event-page-record-grid--splits">
                {statsData.splitRecords.map((split) => (
                  <EventRecordCard
                    key={split.segmentType}
                    label={`Fastest ${formatSegmentTypeLabel(split.segmentType)}`}
                    time={split.fastest.timeFormatted}
                    athleteName={split.fastest.athleteName}
                    athleteId={split.fastest.athleteId}
                    detail={
                      split.avgTimeFormatted
                        ? `Average ${split.avgTimeFormatted}`
                        : undefined
                    }
                    tone="split"
                  />
                ))}
              </div>
            ) : null}
          </section>

          <section className="event-page-section">
            <div className="event-page-results-card">
              <div className="event-page-panel-header">
                <div>
                  <h2 className="event-page-section-title">Results</h2>
                  <p className="event-page-section-copy">
                    Search within the loaded page of results and jump straight to
                    athletes or detailed result pages.
                  </p>
                </div>
                <p className="event-page-panel-kicker">
                  {filteredResults.length.toLocaleString()} shown
                  {filteredResults.length !== results.length
                    ? ` of ${results.length.toLocaleString()} loaded`
                    : ""}
                </p>
              </div>

              <div className="event-page-results-toolbar">
                <Input
                  type="search"
                  value={tableSearch}
                  onChange={(event) => setTableSearch(event.target.value)}
                  placeholder="Search athlete, bib, or location"
                  aria-label="Search event results table"
                  className="w-full sm:max-w-sm"
                />
                <Combobox
                  items={ageGroups}
                  value={ageGroup || null}
                  onValueChange={(value) => setAgeGroup(value ?? "")}
                >
                  <ComboboxInput
                    aria-label="Filter event results by age group"
                    placeholder="All Event Age Groups"
                    className="w-full sm:w-[240px]"
                    showClear={Boolean(ageGroup)}
                  />
                  <ComboboxContent>
                    <ComboboxEmpty>No age groups found.</ComboboxEmpty>
                    <ComboboxList>
                      {(ag) => (
                        <ComboboxItem key={ag} value={ag}>
                          {ag}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </div>

              <div className="event-page-results-table-shell">
                <div className="event-page-results-table-wrap">
                  <Table>
                    <TableHeader className="event-page-results-header">
                      <TableRow>
                        <TableHead className="pl-3 pr-4">#</TableHead>
                        <TableHead className="pr-4">Athlete</TableHead>
                        <TableHead className="pr-4">Location</TableHead>
                        <TableHead className="pr-4">Gender</TableHead>
                        <TableHead className="pr-4">Age Group</TableHead>
                        <TableHead className="pr-4 text-right">Time</TableHead>
                        <TableHead className="pr-3 text-right">Bib</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredResults.length === 0 ? (
                        <TableRow className="event-page-result-row">
                          <TableCell
                            colSpan={7}
                            className="py-6 text-center text-sm text-muted-foreground"
                          >
                            No results match the current search/filter.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredResults.map((result) => {
                          const isHighlighted = highlight === result.id;
                          const hideRank = shouldHideRank(
                            result.finishRankOverall,
                            result,
                          );
                          const flagIso =
                            result.athleteRepresentingCountryIso2 ??
                            result.athleteCountryIso2;
                          const status = getResultStatus(result);

                          return (
                            <TableRow
                              key={result.id}
                              ref={isHighlighted ? highlightRef : undefined}
                              className={`event-page-result-row${isHighlighted ? " is-highlighted" : ""}`}
                            >
                              <TableCell className="pl-3 pr-4">
                                {hideRank
                                  ? "—"
                                  : (result.finishRankOverall ?? "—")}
                              </TableCell>
                              <TableCell className="pr-4">
                                <div className="event-page-athlete-cell">
                                  <Link
                                    to="/results/$resultId"
                                    params={{ resultId: result.id }}
                                    className="event-page-athlete-link"
                                  >
                                    {flagIso ? (
                                      <ReactCountryFlag
                                        countryCode={flagIso}
                                        svg
                                        style={{
                                          width: "1em",
                                          height: "1em",
                                          marginRight: "0.4em",
                                        }}
                                      />
                                    ) : null}
                                    {result.athleteName}
                                  </Link>
                                  {status ? (
                                    <span className="event-page-status-pill">
                                      {status}
                                    </span>
                                  ) : null}
                                </div>
                              </TableCell>
                              <TableCell className="pr-4 text-muted-foreground">
                                {[
                                  result.athleteCity,
                                  result.athleteState,
                                  result.athleteCountryIso2,
                                ]
                                  .filter(Boolean)
                                  .join(", ") || "—"}
                              </TableCell>
                              <TableCell className="pr-4 text-muted-foreground">
                                {result.athleteGender ?? "—"}
                              </TableCell>
                              <TableCell className="pr-4">
                                <span className="event-page-age-group-pill">
                                  {result.ageGroup ?? "—"}
                                </span>
                              </TableCell>
                              <TableCell className="pr-4 text-right font-mono">
                                {result.finishTimeFormatted ?? "—"}
                              </TableCell>
                              <TableCell className="pr-3 text-right text-muted-foreground">
                                {result.bib ?? "—"}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <PaginationControls
                page={currentPage}
                pageSize={EVENT_RESULTS_PER_PAGE}
                currentCount={filteredResults.length}
                hasNextPage={results.length === EVENT_RESULTS_PER_PAGE}
                onPageChange={setPage}
                itemLabel="results"
              />
            </div>
          </section>

          <section className="event-page-section">
            <div className="event-page-section-header">
              <div>
                <h2 className="event-page-section-title">Stats</h2>
                <p className="event-page-section-copy">
                  Participation, split distribution, and age-group composition
                  for this event.
                </p>
              </div>
            </div>

            {ageGroups.length > 0 ? (
              <div className="event-page-chart-card">
                <SplitDistributionChart
                  ageGroups={ageGroups}
                  selectedAgeGroup={splitAgeGroup}
                  onAgeGroupChange={setSplitAgeGroup}
                  splits={splitDistributionSplits}
                  isFetching={ageGroupSplitsFetching}
                  title={splitDistributionSplits.some((s) => s.segmentType === "finish") ? "Finish Time Distribution" : "Age Group Split Distribution"}
                  description={splitDistributionSplits.some((s) => s.segmentType === "finish") ? "Explore finish time distributions for a selected age group." : "Explore swim, bike, and run split distributions for a selected age group."}
                />
              </div>
            ) : null}

            <div className="event-page-chart-card">
              <div className="event-page-panel-header">
                <div>
                  <h3 className="event-page-panel-title">Participation</h3>
                  <p className="event-page-panel-copy">
                    Finishers and non-finish outcomes for the field.
                  </p>
                </div>
              </div>
              <div className="event-page-totals-grid">
                <div className="event-page-total-card">
                  <p className="event-page-total-value">
                    {statsData.participation.totalFinishers.toLocaleString()}
                  </p>
                  <p className="event-page-total-label">Finishers</p>
                </div>
                <div className="event-page-total-card">
                  <p className="event-page-total-value">
                    {statsData.participation.maleCount.toLocaleString()}
                  </p>
                  <p className="event-page-total-label">Male</p>
                </div>
                <div className="event-page-total-card">
                  <p className="event-page-total-value">
                    {statsData.participation.femaleCount.toLocaleString()}
                  </p>
                  <p className="event-page-total-label">Female</p>
                </div>
                {statsData.participation.totalDNF > 0 ? (
                  <div className="event-page-total-card">
                    <p className="event-page-total-value">
                      {statsData.participation.totalDNF.toLocaleString()}
                    </p>
                    <p className="event-page-total-label">DNF</p>
                  </div>
                ) : null}
                {statsData.participation.totalDNS > 0 ? (
                  <div className="event-page-total-card">
                    <p className="event-page-total-value">
                      {statsData.participation.totalDNS.toLocaleString()}
                    </p>
                    <p className="event-page-total-label">DNS</p>
                  </div>
                ) : null}
                {statsData.participation.totalDQ > 0 ? (
                  <div className="event-page-total-card">
                    <p className="event-page-total-value">
                      {statsData.participation.totalDQ.toLocaleString()}
                    </p>
                    <p className="event-page-total-label">DQ</p>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="event-page-chart-card">
              <AgeGroupParticipationChart
                data={ageGroupParticipationData}
                titleClassName="event-page-panel-title"
                className="event-page-age-group-chart"
                description="Finishers per age group, split by male and female fields."
                withInnerChartBorder
              />
            </div>
          </section>
        </>
      ) : (
        <section className="event-page-section">
          <div className="event-page-empty-state">
            <p className="event-page-empty-copy">
              {isStatsLoading
                ? "Loading event stats..."
                : statsError instanceof Error
                  ? "Event stats are unavailable right now. Results are still listed below."
                  : "Event stats are unavailable right now. Results are still listed below."}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
